/*
 * HomeShowcase — Iter 88.3 + Iter 89
 * ====================================================================
 * Two homepage sections that celebrate club history + partners:
 *   • TrophyCabinet  — every closed season stacked as a badge card
 *                      (final position, top scorer, competitions).
 *   • SponsorsStrip  — rich sponsor cards grouped by tier
 *                      (Iter 89 redesigned to match the sponsor page).
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Trophy, Award, Crown, Star, ExternalLink, Medal, Heart, ChevronRight, Globe,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================
// Season badge — one card per archived season
// ============================================================
const SeasonBadge = ({ season }) => {
  const pos = season.final_position;
  // Winners get a crown, top-3 an award, top-8 a trophy
  const rank = pos ? (pos === 1 ? "champion" : pos <= 3 ? "podium" : pos <= 8 ? "top" : "regular") : "regular";
  const rankColor = rank === "champion" ? "#FFD700"
                  : rank === "podium"   ? "#C0C0C0"
                  : rank === "top"      ? "#CD7F32"
                  : "#71717a";
  const RankIcon = rank === "champion" ? Crown : rank === "podium" ? Award : Trophy;

  const teamLabel = season.team_type === "Academy" ? "Ακαδημία" : "Α' Ομάδα";
  const teamColor = season.team_type === "Academy" ? "#10B981" : "#3B82F6";

  return (
    <Link
      to={`/seasons`}
      className="group flex-shrink-0 w-72 relative bg-gradient-to-b from-[#141414] to-[#0a0a0a] border border-[#262626] rounded-2xl p-5 hover:border-[#F5A623]/50 transition-all"
      data-testid={`trophy-season-${season.id}`}
    >
      {/* Rank glow */}
      <div className="absolute inset-0 rounded-2xl opacity-30 pointer-events-none transition-opacity group-hover:opacity-60"
           style={{ background: `radial-gradient(circle at top, ${rankColor}22, transparent 60%)` }} />

      <div className="relative flex items-start justify-between mb-3">
        <div>
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: teamColor }}>
            {teamLabel}
          </span>
          <div className="font-['Bebas_Neue'] text-3xl text-white leading-tight mt-1">{season.name}</div>
        </div>
        <RankIcon size={28} style={{ color: rankColor }} />
      </div>

      <div className="relative space-y-2.5 mt-4">
        {pos && (
          <div className="flex items-center justify-between border-b border-[#1e1e1e] pb-2">
            <span className="text-[11px] text-zinc-500 uppercase tracking-wider">Θέση</span>
            <span className="font-['Bebas_Neue'] text-2xl" style={{ color: rankColor }}>
              {pos === 1 ? "🏆 " : ""}#{pos}
            </span>
          </div>
        )}
        {season.snapshot_top_scorer && (
          <div className="flex items-start gap-2">
            <Star size={12} className="text-[#F5A623] mt-1 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Πρώτος Σκόρερ</div>
              <div className="text-sm text-white truncate">{season.snapshot_top_scorer}</div>
              <div className="text-xs text-[#F5A623] font-bold">{season.snapshot_top_scorer_goals} γκολ</div>
            </div>
          </div>
        )}
        {(season.competitions || []).length > 0 && (
          <div>
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Διοργανώσεις</div>
            <div className="flex flex-wrap gap-1">
              {(season.competitions || []).slice(0, 3).map((c, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-300 truncate max-w-[110px]" title={c}>{c}</span>
              ))}
            </div>
          </div>
        )}
        <div className="pt-2 border-t border-[#1e1e1e] flex items-center justify-between text-[10px] text-zinc-500">
          <span>{season.snapshot_fixtures_count ?? 0} αγώνες</span>
          <span>{season.snapshot_players_count ?? 0} παίκτες</span>
        </div>
      </div>
    </Link>
  );
};

// ============================================================
// Trophy Cabinet — horizontal scrolling stack of season badges
// ============================================================
export const TrophyCabinet = () => {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/seasons`);
        // Only archived seasons that have meaningful data (fixtures, standings or a top scorer).
        const filtered = (r.data || []).filter(s => s.is_archived && (
          (s.snapshot_fixtures_count || 0) > 0 ||
          s.snapshot_top_scorer ||
          (s.competitions || []).length > 0
        ));
        // Newest first
        filtered.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
        setSeasons(filtered);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || seasons.length === 0) return null;

  return (
    <section className="py-14 sm:py-20 relative overflow-hidden" data-testid="trophy-cabinet">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a] to-transparent" />
      <div className="max-w-7xl mx-auto px-4 relative">
        <div className="text-center mb-10">
          <span className="text-xs text-[#F5A623] tracking-[0.3em] uppercase">Κληροδοτημα</span>
          <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl text-white mt-2">Ιστορικο Σεζον</h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-lg mx-auto">
            Κάθε ολοκληρωμένη σεζόν προστίθεται εδώ — η ιστορία της ομάδας χτίζεται μπροστά στα μάτια σου.
          </p>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-4 scroll-smooth snap-x snap-mandatory" style={{ scrollbarWidth: "thin" }}>
          {seasons.map(s => (
            <div key={s.id} className="snap-start">
              <SeasonBadge season={s} />
            </div>
          ))}
        </div>

        <div className="text-center mt-6">
          <Link to="/seasons" className="text-sm text-[#F5A623] hover:underline inline-flex items-center gap-1" data-testid="trophy-view-all">
            Δες όλες τις σεζόν <ExternalLink size={12} />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ============================================================
// Sponsors strip — Iter 89 redesign matching /sponsors page style.
// Rich cards grouped by tier, each with:
//   · Tier chip (MEGA / GOLD / SILVER / SUPPORTER)
//   · Team scope chip (Α' Ομάδα / Ακαδημία)
//   · Logo, name, description, website + "Δείτε Περισσότερα"
// ============================================================
const TIER = {
  mega:      { label: "MEGA SPONSOR",   color: "#F5A623", icon: Crown },
  gold:      { label: "GOLD SPONSOR",   color: "#EAB308", icon: Award },
  silver:    { label: "SILVER SPONSOR", color: "#94A3B8", icon: Medal },
  supporter: { label: "SUPPORTER",      color: "#10B981", icon: Heart },
};
const TIER_ORDER = ["mega", "gold", "silver", "supporter"];

const scopeLabel = (t) => t === "academy" ? "Ακαδημία" : t === "first_team" ? "Α' Ομάδα" : "Γενικός";
const scopeColor = (t) => t === "academy" ? "#10B981" : t === "first_team" ? "#3B82F6" : "#F5A623";

const SponsorCard = ({ sponsor }) => {
  const tier = TIER[sponsor.level] || TIER.supporter;
  const TierIcon = tier.icon;
  const logo = sponsor.logo_url
    ? (sponsor.logo_url.startsWith("http") ? sponsor.logo_url : `${process.env.REACT_APP_BACKEND_URL}${sponsor.logo_url}`)
    : null;

  return (
    <Link
      to={`/sponsors/${sponsor.id}`}
      className="group relative bg-gradient-to-b from-[#141414] to-[#0a0a0a] border border-[#262626] rounded-2xl overflow-hidden hover:border-[#F5A623]/40 transition-all"
      data-testid={`home-sponsor-${sponsor.id}`}
    >
      {/* Tier accent line at top */}
      <div className="h-1 w-full" style={{ background: tier.color }} />

      <div className="p-5">
        {/* Chips row */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span
            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded uppercase tracking-wider font-bold"
            style={{ background: `${tier.color}22`, color: tier.color, border: `1px solid ${tier.color}55` }}
          >
            <TierIcon size={10} /> {tier.label}
          </span>
          <span
            className="text-[10px] px-2 py-0.5 rounded uppercase tracking-wider"
            style={{ background: `${scopeColor(sponsor.sponsor_type)}22`, color: scopeColor(sponsor.sponsor_type), border: `1px solid ${scopeColor(sponsor.sponsor_type)}55` }}
          >
            {scopeLabel(sponsor.sponsor_type)}
          </span>
        </div>

        {/* Logo + name */}
        <div className="flex items-center gap-4 mb-3">
          <div className="w-16 h-16 bg-white/95 rounded-xl flex items-center justify-center p-2 flex-shrink-0 grayscale group-hover:grayscale-0 transition-all duration-500">
            {logo ? (
              <img src={logo} alt={sponsor.name} className="max-w-full max-h-full object-contain" loading="lazy" />
            ) : (
              <span className="text-[10px] text-zinc-500 text-center">{sponsor.name}</span>
            )}
          </div>
          <div className="min-w-0">
            <h3 className="font-['Bebas_Neue'] text-2xl text-white leading-tight tracking-wide truncate" title={sponsor.name}>
              {sponsor.name}
            </h3>
            {sponsor.website && (
              <span className="text-[11px] text-zinc-500 flex items-center gap-1 mt-0.5 truncate">
                <Globe size={11} /> {sponsor.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              </span>
            )}
          </div>
        </div>

        {/* Description */}
        {sponsor.description && (
          <p className="text-sm text-zinc-400 line-clamp-3 leading-relaxed mb-4">{sponsor.description}</p>
        )}

        {/* CTA */}
        <div className="flex items-center justify-between pt-3 border-t border-[#1e1e1e]">
          <span className="text-xs text-[#F5A623] group-hover:text-[#F5A623] flex items-center gap-1 font-medium">
            Δείτε Περισσότερα <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
          </span>
          {sponsor.website && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(sponsor.website, "_blank", "noopener,noreferrer"); }}
              className="text-[10px] text-zinc-500 hover:text-white flex items-center gap-1"
              data-testid={`home-sponsor-external-${sponsor.id}`}
              aria-label="Ανοιγμα ιστοσελιδας χορηγου"
            >
              <ExternalLink size={10} />
            </button>
          )}
        </div>
      </div>
    </Link>
  );
};

export const SponsorsStrip = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/sponsors`);
        // Iter 91.4 — show every admin-added sponsor (Α' Ομάδα + Ακαδημία),
        // ordered by tier (mega → gold → silver → supporter). Historically we
        // were filtering to mega/gold only, which hid real entries on prod.
        const all = (r.data || []).filter(s => s && s.name);
        const rank = { mega: 0, gold: 1, silver: 2, supporter: 3 };
        all.sort((a, b) => (rank[a.level] ?? 9) - (rank[b.level] ?? 9));
        setSponsors(all);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  // Auto-rotate every 6s. Reset any time the user manually picks a slide via dots
  // so their choice isn't immediately overridden.
  useEffect(() => {
    if (sponsors.length < 2) return;
    const id = setInterval(() => setIdx(i => (i + 1) % sponsors.length), 6000);
    return () => clearInterval(id);
  }, [sponsors.length, idx]);

  if (loading || sponsors.length === 0) return null;

  const current = sponsors[idx];

  return (
    <section className="py-16 sm:py-20 bg-[#050505] border-t border-[#141414]" data-testid="home-sponsors-strip">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-10">
          <span className="text-xs text-[#F5A623] tracking-[0.3em] uppercase">Στηριζουν τη Λευτερια FC</span>
          <h2 className="font-['Bebas_Neue'] text-4xl sm:text-5xl text-white mt-2">Οι Χορηγοι μας</h2>
        </div>

        <div className="public-sponsor-logos" data-testid="public-sponsor-logos">{sponsors.filter(s => s.logo_url || s.image_url).map(s => <Link key={s.id} to={`/sponsors/${s.id}`} data-testid={`public-sponsor-logo-${s.id}`}><img src={(s.logo_url || s.image_url).startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${s.logo_url || s.image_url}` : s.logo_url || s.image_url} alt={s.name} loading="lazy" /></Link>)}</div>
        <div className="relative">
          <div key={current.id} className="animate-fadeIn" data-testid={`home-sponsor-slide-${current.id}`}>
            <SponsorCard sponsor={current} />
          </div>

          {sponsors.length > 1 && (
            <div className="flex justify-center gap-1.5 mt-5" data-testid="home-sponsor-dots">
              {sponsors.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setIdx(i)}
                  className={`transition-all rounded-full ${
                    i === idx ? "w-6 h-1.5 bg-[#F5A623]" : "w-1.5 h-1.5 bg-zinc-700 hover:bg-zinc-500"
                  }`}
                  aria-label={`Χορηγός ${i + 1}`}
                  data-testid={`home-sponsor-dot-${s.id}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
